import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconSearch } from '@u21/tabler-icons';

// Selectors
import { selectHasReadRulesPermission } from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatRuleOptions } from 'app/modules/search/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { RuleSearchPayload, SearchFilter } from 'app/modules/search/payload';
import { RuleStatuses } from 'app/modules/rules/models';
import { MatchType, RuleSearchOptions } from 'app/modules/search/enum';
import { RuleSource } from 'app/modules/search/response';
import { RULE_QUERY_KEYS } from 'app/modules/rules/queries/keys';
import {
  useRuleSearch,
  searchRule,
} from 'app/modules/rules/queries/useRuleSearch';
import { getRuleSearchConditions } from 'app/modules/search/utils';

export type U21RuleSearchProps<TClearable extends boolean> = DistributiveOmit<
  U21SelectSearchProps<number, TClearable>,
  'options' | 'onSearch'
> & {
  excludeSyncRules?: boolean;
  allowedStatuses?: RuleStatuses[];
};

export const U21RuleSearch = <TClearable extends boolean>({
  allowedStatuses,
  excludeSyncRules = false,
  ...rest
}: U21RuleSearchProps<TClearable>) => {
  const { value } = rest;
  const queryClient = useQueryClient();

  const filters = useMemo(() => {
    const filter: SearchFilter<RuleSource>[] = [];

    if (allowedStatuses && allowedStatuses.length) {
      filter.push({
        field: 'status',
        phrases: allowedStatuses,
      });
    }

    if (excludeSyncRules) {
      filter.push({
        field: 'is_synchronous',
        phrases: ['false'],
      });
    }

    return filter.length ? filter : undefined;
  }, [allowedStatuses, excludeSyncRules]);

  const [payload, setPayload] = useState<RuleSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const hasReadRulesPermission = useSelector(selectHasReadRulesPermission);

  const {
    query: { isFetching: loading, isError },
    refreshRules,
    rules: data,
  } = useRuleSearch(payload, filters);

  const options = useMemo(() => formatRuleOptions(data), [data]);

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadRulesPermission) {
      return;
    }

    let missingValues: number[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      const initialPayload: RuleSearchPayload = {
        offset: OFFSET_DEFAULT,
        limit: missingValues.length,
        search_conditions: [
          {
            field: RuleSearchOptions.ID,
            match_type: MatchType.EXACT,
            phrases: missingValues.map(String),
          },
        ],
        filters,
      };

      queryClient.prefetchQuery({
        queryKey: RULE_QUERY_KEYS.getRuleSearch(initialPayload),
        queryFn: () => searchRule(initialPayload),
      });

      setPayload(initialPayload);
    }
  }, [
    allowedStatuses,
    excludeSyncRules,
    filters,
    hasReadRulesPermission,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        setPayload({
          offset: OFFSET_DEFAULT,
          limit: INFINITE_SCROLL_LIMIT,
          search_conditions: getRuleSearchConditions([query]),
          filters,
        });
      }
    },

    [filters],
  );

  if (!hasReadRulesPermission) {
    return null;
  }

  let noOptionsText = 'Type to search';
  if (isError) {
    noOptionsText = 'Search failed. Please try again.';
  } else if (searchValue) {
    noOptionsText = 'No results found';
  }

  return (
    <U21SelectSearch
      loading={loading}
      options={options}
      onExactSearch={async (searchTerms) => {
        const searchPayload: RuleSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: searchTerms.length,
          search_conditions: [
            {
              field: RuleSearchOptions.ID,
              match_type: MatchType.EXACT,
              phrases: searchTerms,
            },
          ],
          filters,
        };

        await queryClient.prefetchQuery({
          queryKey: RULE_QUERY_KEYS.getRuleSearch(searchPayload),
          queryFn: () => searchRule(searchPayload),
        });
        refreshRules();
      }}
      onSearch={onSearch}
      startIcon={<IconSearch />}
      noOptionsText={noOptionsText}
      {...rest}
    />
  );
};
